/**
 * Μαζικές ενέργειες πλέγματος (§13.11).
 *
 * ΚΑΝΟΝΑΣ: κάθε ενέργεια δείχνει **πόσες γραμμές αλλαγής θα δημιουργήσει και
 * πόσα δίδυμα θα παρασύρει ΠΡΙΝ** εφαρμοστεί. Ένα «κάν' τα διαθέσιμα» πάνω στα 86
 * μη διαθέσιμα του κατ. 23 ενεργοποιεί και είδη ανενεργών προσφορών (§13.1) — ο
 * χειριστής πρέπει να το δει ως αριθμό πριν πατήσει, όχι ως blocker μετά.
 *
 * Η προ-μέτρηση χρησιμοποιεί ΤΙΣ ΙΔΙΕΣ συναρτήσεις με την εφαρμογή (catalogUi),
 * αλλιώς θα έλεγε άλλα από όσα στέλνονται.
 */

import React, { useMemo, useState } from 'react';
import { Copy, Eraser, Loader2, Tags, X } from 'lucide-react';
import { EditChange, GridRow, GridStore } from '../../services/platformCatalogApi';
import {
  cellIsEditable, defaultApplyToTwins, fmtMoney, parsePriceInput, platformAvailable, platformPrice,
  shownAvailable, shownPrice, twinCarry,
} from './catalogUi';

type BulkKind = 'price' | 'available' | 'unavailable' | 'copy';

interface Props {
  rows: GridRow[];
  stores: GridStore[];
  selectedRows: Record<string, boolean>;
  selectedStores: Record<string, boolean>;
  canEdit: boolean;
  busy: boolean;
  onApply: (changes: EditChange[]) => void;
  onClearSelection: () => void;
  onClearEdits: (storeIds: number[] | null) => void;
}

interface BulkPlan {
  changes: EditChange[];
  twins: number;
  offerItems: number;
  storesTouched: number;
}

const BulkActionsBar: React.FC<Props> = ({
  rows, stores, selectedRows, selectedStores, canEdit, busy, onApply, onClearSelection, onClearEdits,
}) => {
  const [kind, setKind] = useState<BulkKind>('price');
  const [priceText, setPriceText] = useState('');
  const [sourceStore, setSourceStore] = useState<number | ''>('');

  const targetRows = useMemo(() => rows.filter(r => !!selectedRows[r.master_key]), [rows, selectedRows]);

  /** Χωρίς ρητή επιλογή στηλών η ενέργεια αφορά ΟΛΑ τα καταστήματα του πλέγματος. */
  const explicitStores = stores.filter(s => !!selectedStores[String(s.store_id)]);
  const targetStores = explicitStores.length > 0 ? explicitStores : stores;
  const allStores = explicitStores.length === 0;

  const plan = useMemo<BulkPlan>(() => {
    const changes: EditChange[] = [];
    let twins = 0;
    let offerItems = 0;
    const touched: Record<string, boolean> = {};
    const price = parsePriceInput(priceText);

    for (let i = 0; i < targetRows.length; i++) {
      const row = targetRows[i];
      const cells = row.cells || {};
      const sourcePrice = kind === 'copy' && sourceStore !== ''
        ? shownPrice(cells[String(sourceStore)])
        : null;

      for (let j = 0; j < targetStores.length; j++) {
        const storeId = targetStores[j].store_id;
        const cell = cells[String(storeId)];
        if (!cellIsEditable(cell) || !cell) continue;
        if (kind === 'copy' && sourceStore !== '' && storeId === Number(sourceStore)) continue;

        if (kind === 'price' || kind === 'copy') {
          const value = kind === 'price' ? price : sourcePrice;
          if (value === null) continue;
          // Ο έλεγχος «διαφέρει;» πάνω σε ό,τι ΒΛΕΠΕΙ ο χειριστής· το old_value
          // που φεύγει είναι πάντα η τιμή της πλατφόρμας (§13.6.1) — αλλιώς η
          // δεύτερη διόρθωση του ίδιου κελιού γυρίζει ψεύτικο 409.
          const before = shownPrice(cell);
          if (before !== null && Math.abs(before - value) < 0.0001) continue;
          changes.push({
            store_id: storeId,
            entity_kind: 'item',
            item_code: cell.item_code,
            master_key: row.master_key,
            field: 'price',
            old_value: platformPrice(cell),
            new_value: value,
            apply_to_twins: defaultApplyToTwins(cell, 'price'),
          });
          twins += twinCarry(cell, 'price');
        } else {
          const value = kind === 'available';
          const before = shownAvailable(cell);
          if (before === value) continue;
          changes.push({
            store_id: storeId,
            entity_kind: 'item',
            item_code: cell.item_code,
            master_key: row.master_key,
            field: 'is_available',
            old_value: platformAvailable(cell),
            new_value: value,
            apply_to_twins: defaultApplyToTwins(cell, 'is_available'),
          });
          // Η διαθεσιμότητα παρασύρει δίδυμα όπως η τιμή (§13.3.3) — ο μετρητής
          // πρέπει να το λέει ΠΡΙΝ, γιατί εδώ ακριβώς ξυπνούν κρυφά είδη
          // ανενεργών προσφορών 1+1.
          twins += twinCarry(cell, 'is_available');
          if (cell.in_offer_category || (cell.twin && cell.twin.role === 'offer_shadow')) offerItems += 1;
        }
        touched[String(storeId)] = true;
      }
    }
    return { changes, twins, offerItems, storesTouched: Object.keys(touched).length };
  }, [targetRows, targetStores, kind, priceText, sourceStore]);

  const selectionEmpty = targetRows.length === 0;
  const priceMissing = kind === 'price' && parsePriceInput(priceText) === null;
  const sourceMissing = kind === 'copy' && sourceStore === '';
  const canApply = canEdit && !busy && !selectionEmpty && !priceMissing && !sourceMissing && plan.changes.length > 0;

  return (
    <div className="bg-surface-1/60 border border-line rounded-xl p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-t2 flex items-center gap-1.5">
          <Tags className="w-4 h-4 text-t3" />
          <strong>{targetRows.length}</strong> γραμμές ×{' '}
          <strong>{targetStores.length}</strong> {allStores ? 'καταστήματα (όλα)' : 'επιλεγμένα καταστήματα'}
        </span>

        <select
          value={kind}
          onChange={e => setKind(e.target.value as BulkKind)}
          className="bg-surface-0 border border-line-strong rounded-lg px-3 py-2 text-sm text-t1"
        >
          <option value="price">Ορισμός τιμής</option>
          <option value="available">Διαθέσιμο</option>
          <option value="unavailable">Μη διαθέσιμο</option>
          <option value="copy">Αντιγραφή τιμών από κατάστημα…</option>
        </select>

        {kind === 'price' && (
          <input
            type="text"
            inputMode="decimal"
            value={priceText}
            onChange={e => setPriceText(e.target.value)}
            placeholder="π.χ. 1,70"
            className="w-28 bg-surface-0 border border-line-strong rounded-lg px-3 py-2 text-sm text-t1 text-right"
          />
        )}

        {kind === 'copy' && (
          <select
            value={sourceStore === '' ? '' : String(sourceStore)}
            onChange={e => setSourceStore(e.target.value === '' ? '' : Number(e.target.value))}
            className="bg-surface-0 border border-line-strong rounded-lg px-3 py-2 text-sm text-t1"
          >
            <option value="">Από ποιο κατάστημα…</option>
            {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
          </select>
        )}

        <button
          type="button"
          onClick={() => onApply(plan.changes)}
          disabled={!canApply}
          className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
          Εφαρμογή
        </button>

        <button
          type="button"
          onClick={() => onClearEdits(explicitStores.length > 0 ? explicitStores.map(s => s.store_id) : null)}
          disabled={!canEdit || busy}
          title={explicitStores.length > 0
            ? 'Διαγράφει τις πρόχειρες αλλαγές των επιλεγμένων καταστημάτων'
            : 'Διαγράφει ΟΛΕΣ τις πρόχειρες αλλαγές του οργανισμού'}
          className="px-3 py-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-50 text-t1 border border-line-strong rounded-xl text-sm flex items-center gap-2"
        >
          <Eraser className="w-4 h-4" /> Καθαρισμός αλλαγών
        </button>

        <button
          type="button"
          onClick={onClearSelection}
          className="ml-auto px-2 py-2 text-t3 hover:text-t1 rounded-lg flex items-center gap-1 text-sm"
        >
          <X className="w-4 h-4" /> Καθαρισμός επιλογής
        </button>
      </div>

      {/* Η προ-μέτρηση. Λέει ΑΚΡΙΒΩΣ τι θα γίνει, πριν γίνει. */}
      <div className="text-xs">
        {selectionEmpty ? (
          <span className="text-t4">Διάλεξε γραμμές (και προαιρετικά στήλες καταστημάτων) για μαζική ενέργεια.</span>
        ) : priceMissing ? (
          <span className="text-t4">Γράψε τιμή για να δεις πόσες αλλαγές θα δημιουργηθούν.</span>
        ) : sourceMissing ? (
          <span className="text-t4">Διάλεξε κατάστημα-πηγή για να δεις πόσες αλλαγές θα δημιουργηθούν.</span>
        ) : plan.changes.length === 0 ? (
          <span className="text-t4">Καμία αλλαγή: όλα τα επιλεγμένα κελιά έχουν ήδη αυτή την τιμή/κατάσταση.</span>
        ) : (
          <span className="text-t2">
            Θα δημιουργηθούν <strong>{plan.changes.length}</strong> γραμμές αλλαγής σε{' '}
            <strong>{plan.storesTouched}</strong> καταστήματα
            {kind === 'copy' && sourceStore !== ''
              ? ` (αντιγραφή από «${(stores.filter(s => s.store_id === Number(sourceStore))[0] || { store_name: '' }).store_name}»)`
              : ''}
            {kind === 'price' && parsePriceInput(priceText) !== null
              ? ` στην τιμή ${fmtMoney(parsePriceInput(priceText))} €`
              : ''}
            .
            {plan.twins > 0 && (
              <span className="text-sky-700 dark:text-sky-300">
                {' '}Θα παρασυρθούν <strong>{plan.twins}</strong> δίδυμα (ίδιο όνομα σε κατηγορία προσφοράς,
                ίδια τιμή σήμερα) — παρασύρονται και στην τιμή και στη διαθεσιμότητα.
              </span>
            )}
            {plan.offerItems > 0 && (
              <span className="text-amber-700 dark:text-amber-300">
                {' '}Προσοχή: <strong>{plan.offerItems}</strong> από αυτές αγγίζουν είδη κατηγορίας προσφοράς —
                η διαθεσιμότητά τους αλλάζει τι βλέπει ο πελάτης στην προσφορά.
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default BulkActionsBar;
